import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MandalaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location=0) in vec2 aPos;
out vec2 vUV;
void main(){ vUV=aPos*0.5+0.5; gl_Position=vec4(aPos,0.0,1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p){ p=fract(p*vec2(123.34,456.21)); p+=dot(p,p+45.32); return fract(p.x*p.y); }
float noise21(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f); return mix(mix(hash21(i),hash21(i+vec2(1,0)),f.x),mix(hash21(i+vec2(0,1)),hash21(i+vec2(1,1)),f.x),f.y); }
mat2 rot2(float a){ float c=cos(a),s=sin(a); return mat2(c,-s,s,c); }
float band(float x,float c,float w){ return exp(-pow(abs(x-c)/w,2.0)); }
vec3 palette(float x){ return 0.52+0.48*cos(6.28318*(x+vec3(0.02,0.24,0.48))); }

void main(){
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y); p.x *= uResolution.x / uResolution.y;
  float bass=(uBass.x+uBass.y+uBass.z)/3.0;
  float mid=(uMid.x+uMid.y+uMid.z)/3.0;
  float high=(uHigh.x+uHigh.y+uHigh.z)/3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid=pow(clamp(mid*2.8,0.0,1.0),0.75); high=pow(clamp(high*3.5,0.0,1.0),0.8);
  float r=length(p), a=atan(p.y,p.x), tau=6.2831853;
  float grain=noise21(gl_FragCoord.xy*0.42+uTime*vec2(2.1,-1.3));
  vec3 col=mix(vec3(0.012,0.017,0.035),vec3(0.045,0.025,0.075),0.45+0.45*p.y);
  col+=vec3(0.025,0.04,0.075)*(0.5+0.5*cos(r*5.0-uTime*0.18));
  col+=(grain-0.5)*0.018;

  // Twelve mirrored sectors are the geometric backbone.
  float sector=tau/12.0;
  float fa=abs(mod(a+sector*0.5,sector)-sector*0.5);
  float spoke=exp(-70.0*fa)*(0.25+high*1.5)*smoothstep(1.12,0.10,r);
  col+=palette(a/tau+uTime*0.025)*spoke;

  // Five independently rotating activity rings with filled and engraved structure.
  for(int i=0;i<5;i++){
    float fi=float(i);
    float rr=0.19+fi*0.17+bass*0.012*sin(fi*2.2+uTime*3.0);
    float speed=(mod(fi,2.0)<0.5?1.0:-1.0)*(0.16+fi*0.055);
    float aa=a+uTime*speed;
    float fold=abs(mod(aa+sector*0.5,sector)-sector*0.5);
    float petals=cos(fold*12.0*(2.0+fi))*0.025*(1.0+mid)+sin(aa*24.0-uTime*(0.7+fi))*0.009*high;
    float ring=band(r,rr+petals,0.010+0.004*fi);
    float fill=band(r,rr-0.035,0.036)*(0.5+0.5*cos(fold*(48.0+fi*12.0)+uTime*(1.0+fi)));
    float etch=band(r,rr+0.052,0.004)*(0.5+0.5*cos(aa*(36.0+fi*12.0)-uTime*2.0));
    vec3 rc=palette(fi*0.115+a/tau*0.22+uTime*0.018);
    col+=rc*(ring*(0.9+bass*1.8)+fill*(0.12+mid*0.42)+etch*(0.35+high*1.5));
  }

  // Outer lotus petals bloom with bass, each petal carrying fine inner veins.
  float petalAngle=abs(mod(a+sector*0.5,sector)-sector*0.5)/sector;
  float bloom=0.91+0.15*bass;
  float petalRadius=bloom-0.23*cos(petalAngle*3.14159);
  float petalEdge=band(r,petalRadius,0.012);
  float petalBody=smoothstep(0.07,0.0,abs(r-petalRadius+0.055))*smoothstep(1.0,0.2,petalAngle);
  float veins=(0.5+0.5*cos(petalAngle*36.0+r*75.0-uTime*1.4))*petalBody;
  col+=palette(a/tau+0.62)*petalEdge*(1.4+bass*2.0);
  col+=palette(r*0.25+0.25)*petalBody*(0.14+mid*0.38)+vec3(0.7,0.3,1.0)*veins*high*0.32;

  // Orbiting jewels and opposing satellite crowns.
  for(int i=0;i<12;i++){
    float fi=float(i), ang=fi*sector+uTime*(0.12+mid*0.25);
    vec2 q=p-vec2(cos(ang),sin(ang))*(0.68+0.025*sin(uTime*2.0+fi));
    float d=length(q);
    float jewel=exp(-d*d*1400.0)+0.02/(d*d*220.0+0.018);
    col+=palette(fi/12.0+uTime*0.04)*jewel*(0.25+high*1.8);
  }

  // Pulsing layered core, iris teeth, and rotating seed star.
  float coreR=0.105+0.026*bass+0.008*sin(uTime*4.0);
  float core=exp(-r*r/(coreR*coreR))*1.7;
  float iris=band(r,0.125+0.012*sin(a*12.0-uTime*2.3),0.007);
  float star=band(r,0.055+0.024*cos(fa*12.0),0.009);
  col+=mix(vec3(1.0,0.32,0.55),vec3(0.25,0.8,1.0),mid)*core*(0.45+bass*1.5);
  col+=vec3(1.0,0.75,0.35)*iris*(0.8+high*2.0)+vec3(0.8,0.6,1.0)*star;

  // Fine circular guilloche and drifting dust make quiet passages remain alive.
  float guilloche=pow(0.5+0.5*cos(r*310.0+sin(a*12.0-uTime)*7.0),18.0)*smoothstep(1.2,0.18,r);
  col+=palette(r+a/tau)*guilloche*(0.05+high*0.45);
  vec2 sid=floor((p+1.4)*75.0); vec2 sf=fract((p+1.4)*75.0)-0.5;
  float dust=step(0.982,hash21(sid))*exp(-dot(sf,sf)*90.0)*(0.4+0.6*sin(uTime*3.0+hash21(sid)*20.0));
  col+=vec3(0.55,0.7,1.0)*dust*(0.08+high);
  col *= 1.0 - dot(p, p) * 0.3;
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14); col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2)); outColor = vec4(col, 1.0);
}`;

export function computeMandalaBands(t:number,out:Float32Array):void{ for(let i=0;i<9;i++){ const f=0.55+i*0.34; out[i]=Math.max(0,Math.min(1,0.28+0.22*Math.sin(t*f+i*1.31)+0.28*Math.pow(Math.max(0,Math.sin(t*(1.1+i*0.17)+i)),3))); } }
export function computeMandalaRealtimeBands(freqData:Uint8Array,out:Float32Array):void{ const ranges=[[0,.015],[.015,.04],[.04,.08],[.08,.16],[.16,.28],[.28,.43],[.43,.62],[.62,.8],[.8,.98]]; for(let i=0;i<9;i++){let s=0,n=0; for(let k=Math.floor(ranges[i][0]*freqData.length);k<Math.max(1,Math.floor(ranges[i][1]*freqData.length));k++){s+=freqData[k]||0;n++;} out[i]=n?s/n/255:0;} }
export function createMandalaPipeline(gl:WebGL2RenderingContext):MandalaPipeline|null{ const program=linkProgram(gl,VERT_SRC,FRAG_SRC,POST_ATTRIBS); if(!program)return null; const vao=gl.createVertexArray(),vbo=gl.createBuffer(); if(!vao||!vbo)return null; gl.bindVertexArray(vao);gl.bindBuffer(gl.ARRAY_BUFFER,vbo);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);gl.enableVertexAttribArray(POST_ATTRIBS.aPos);gl.vertexAttribPointer(POST_ATTRIBS.aPos,2,gl.FLOAT,false,0,0);gl.bindVertexArray(null);return{program,vao,vbo,bands:new Float32Array(9)}; }
export function drawMandala(gl:WebGL2RenderingContext,pipe:MandalaPipeline,width:number,height:number,t:number):void{gl.useProgram(pipe.program);const p=pipe.program;gl.uniform2f(gl.getUniformLocation(p,"uResolution"),width,height);gl.uniform1f(gl.getUniformLocation(p,"uTime"),t);gl.uniform3f(gl.getUniformLocation(p,"uBass"),pipe.bands[0],pipe.bands[1],pipe.bands[2]);gl.uniform3f(gl.getUniformLocation(p,"uMid"),pipe.bands[3],pipe.bands[4],pipe.bands[5]);gl.uniform3f(gl.getUniformLocation(p,"uHigh"),pipe.bands[6],pipe.bands[7],pipe.bands[8]);gl.bindVertexArray(pipe.vao);gl.drawArrays(gl.TRIANGLES,0,6);gl.bindVertexArray(null);}
